import { useState, useEffect, useMemo } from 'react';
import { Plus, X, Sun, Moon } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Input, Field } from '@/components/ui/Input';
import { cn } from '@/lib/cn';

const COMMON_TZ = [
  'Asia/Kolkata',
  'America/New_York',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Berlin',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Asia/Dubai',
  'Australia/Sydney',
  'America/Sao_Paulo',
  'UTC',
];

function getZones(): string[] {
  try {
    const intl = Intl as typeof Intl & { supportedValuesOf?: (input: string) => string[] };
    return intl.supportedValuesOf?.('timeZone') ?? COMMON_TZ;
  } catch {
    return COMMON_TZ;
  }
}

function localISO(d: Date): string {
  const tzOff = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - tzOff).toISOString().slice(0, 16);
}

export function TimezoneConverter() {
  const [zones, setZones] = useState<string[]>([]);
  const [selected, setSelected] = useState<string[]>([
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    'America/New_York',
    'Europe/London',
    'Asia/Singapore',
  ]);
  const [when, setWhen] = useState<Date>(new Date());
  const [auto, setAuto] = useState(true);

  useEffect(() => {
    setZones(getZones());
  }, []);

  useEffect(() => {
    if (!auto) return;
    const t = setInterval(() => setWhen(new Date()), 1000);
    return () => clearInterval(t);
  }, [auto]);

  function add(tz: string) {
    if (selected.includes(tz)) return;
    setSelected((s) => [...s, tz]);
  }
  function remove(tz: string) {
    setSelected((s) => s.filter((x) => x !== tz));
  }

  const all = useMemo(() => zones, [zones]);

  return (
    <div>
      <ToolHeader
        toolId="timezone-converter"
        title="Timezone converter"
        description="See a moment across multiple timezones — schedule meetings, sync teams, plan launches."
      />

      <Card className="mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
          <Field label="Reference time">
            <Input
              type="datetime-local"
              value={localISO(when)}
              onChange={(e) => {
                if (e.target.value) {
                  setWhen(new Date(e.target.value));
                  setAuto(false);
                }
              }}
            />
          </Field>
          <Button
            variant={auto ? 'primary' : 'secondary'}
            onClick={() => {
              setAuto((a) => {
                const next = !a;
                if (next) setWhen(new Date());
                return next;
              });
            }}
          >
            {auto ? 'Live' : 'Resume live'}
          </Button>
        </div>
      </Card>

      <div className="space-y-2 mb-4">
        {selected.map((tz) => (
          <ZoneRow key={tz} tz={tz} when={when} onRemove={() => remove(tz)} />
        ))}
      </div>

      <Card>
        <Field label="Add a timezone">
          <Select
            value=""
            onChange={(e) => {
              if (e.target.value) add(e.target.value);
              e.target.value = '';
            }}
            options={[
              { value: '', label: 'Select…' },
              ...all
                .filter((z) => !selected.includes(z))
                .map((z) => ({ value: z, label: z })),
            ]}
          />
        </Field>
      </Card>
    </div>
  );
}

function ZoneRow({ tz, when, onRemove }: { tz: string; when: Date; onRemove: () => void }) {
  const time = useMemo(() => {
    return new Intl.DateTimeFormat('en', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(when);
  }, [tz, when]);

  const date = useMemo(() => {
    return new Intl.DateTimeFormat('en', {
      timeZone: tz,
      weekday: 'short',
      day: '2-digit',
      month: 'short',
    }).format(when);
  }, [tz, when]);

  const hour = parseInt(time.split(':')[0], 10);
  const isDay = hour >= 6 && hour < 18;

  const offset = useMemo(() => {
    try {
      const f = new Intl.DateTimeFormat('en', { timeZone: tz, timeZoneName: 'shortOffset' });
      const part = f.formatToParts(when).find((p) => p.type === 'timeZoneName');
      return part?.value ?? '';
    } catch {
      return '';
    }
  }, [tz, when]);

  const [city, region] = tz.split('/').reverse();

  return (
    <Card className="!p-4 group">
      <div className="flex items-center gap-4">
        <div
          className={cn(
            'size-10 rounded-lg flex items-center justify-center',
            isDay ? 'bg-amber-500/15 text-amber-500' : 'bg-indigo-500/15 text-indigo-400',
          )}
        >
          {isDay ? <Sun size={16} /> : <Moon size={16} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">
            {city.replace(/_/g, ' ')}
            {region && <span className="text-fg-subtle ml-1.5">· {region}</span>}
          </div>
          <div className="text-xs text-fg-muted mt-0.5">
            {date} · {offset}
          </div>
        </div>
        <div className="font-mono text-2xl tabular-nums shrink-0">{time}</div>
        <button
          onClick={onRemove}
          className="size-7 rounded text-fg-subtle hover:text-danger hover:bg-surface-hover transition opacity-0 group-hover:opacity-100"
        >
          <X size={12} />
        </button>
      </div>
    </Card>
  );
}

export default TimezoneConverter;
